import { useCallback, useEffect, useMemo, useState } from "react";
import RbacService, { AdminUser, Role } from "@/app/_services/RbacService";
import { toast } from "@/app/_utils/toast";

export default function useUsersModel() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editing, setEditing] = useState<AdminUser | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [nextUsers, nextRoles] = await Promise.all([
        RbacService.getUsersAsync(),
        RbacService.getRolesAsync(),
      ]);
      setUsers(nextUsers);
      setRoles(nextRoles);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load users");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return users.filter((user) => {
      if (statusFilter !== "all" && user.status !== statusFilter) return false;
      if (!query) return true;
      const name = `${user.first_name} ${user.last_name}`.trim().toLowerCase();
      return user.email.toLowerCase().includes(query) || name.includes(query);
    });
  }, [users, search, statusFilter]);

  async function run(action: () => Promise<unknown>, success: string) {
    try {
      await action();
      toast.success(success);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Something went wrong");
    }
  }

  return {
    users: filtered,
    roles,
    isLoading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    inviteOpen,
    setInviteOpen,
    editing,
    setEditing,
    reload: load,
    resendInvite: (user: AdminUser) =>
      run(() => RbacService.resendInviteAsync(user.id), `Invitation resent to ${user.email}`),
    suspendUser: (user: AdminUser) =>
      run(() => RbacService.suspendUserAsync(user.id), `${user.email} suspended`),
    reactivateUser: (user: AdminUser) =>
      run(() => RbacService.reactivateUserAsync(user.id), `${user.email} restored`),
    deleteUser: (user: AdminUser) =>
      run(() => RbacService.deleteUserAsync(user.id), `${user.email} deleted`),
  };
}
